/**
 * SINGLE SOURCE OF TRUTH for all category-to-route mappings
 * This file ensures consistent route generation across the entire application
 */

import { CourseType } from './routeUtils';

/**
 * Complete mapping of database categories to route prefixes
 * Covers all 31+ unique categories in the database
 */
export const CATEGORY_ROUTE_MAP: Record<string, string> = {
  // Core Technology Categories
  'react': '/courses',
  'sql': '/sql-courses',
  'python': '/python-courses',
  'ai': '/ai-courses',
  'java': '/java-courses',
  'javascript': '/javascript-courses',
  'dotnet': '/dotnet-courses',
  'csharp': '/dotnet-courses', // C# maps to .NET
  
  // Cloud & Infrastructure
  'aws': '/aws-courses',
  'azure': '/azure-courses',
  'gcp': '/gcp-courses',
  'cloud': '/cloud-courses', // Cloud computing courses
  'devops': '/courses',
  'infrastructure': '/infrastructure-courses',
  'serverless': '/serverless-edge-courses',
  'serverless-edge': '/serverless-edge-courses',
  'edge': '/serverless-edge-courses',
  
  // Data & Analytics
  'data-science': '/datascience-courses', // Critical fix - hyphenated version
  'datascience': '/datascience-courses',
  'data-engineering': '/dataengineering-courses', // Critical fix - hyphenated version
  'dataengineering': '/dataengineering-courses',
  'ml': '/ml-courses',
  'powerbi': '/powerbi-courses',
  'tableau': '/tableau-courses',
  'etl': '/etl-courses',
  'database': '/sql-courses', // Generic database maps to SQL
  
  // Automation & Integration
  'workflow-automation': '/workflow-automation-courses',
  'rpa-tools': '/rpa-tools-courses',
  'rpa': '/rpa-tools-courses',
  'n8n': '/workflow-automation-courses',
  'zapier': '/workflow-automation-courses',
  'make': '/workflow-automation-courses',
  
  // Security & Testing
  'appsec': '/courses',
  'security': '/courses',
  'testing': '/testing-modern-courses',
  'testing-modern': '/testing-modern-courses',
  'selenium': '/testing-modern-courses',
  
  // Advanced Categories
  'frontend-advanced': '/frontend-advanced-courses',
  'web3-development': '/web3-development-courses',
  'blockchain': '/courses',
  'game-dev': '/courses',
  'mobile': '/courses',
  'graphics': '/courses',
  'ui-ux': '/courses',
  
  // Enterprise & Professional
  'backend': '/courses',
  'enterprise': '/courses',
  'professional-dev': '/professional-development-courses',
  'specialized-ai': '/ai-courses',
  'programming': '/programming-courses',
  
  // New expanded categories - CRITICAL FIX
  'rust': '/rust-courses',
  'kubernetes-mastery': '/kubernetes-mastery-courses',
  'modern-frontend': '/modern-frontend-courses',
  'cross-platform': '/cross-platform-courses',
  'edge-computing': '/edge-computing-courses',
  'green-tech': '/green-tech-courses',
  'no-code': '/no-code-courses',
  'advanced-api': '/advanced-api-courses',
  'automation': '/workflow-automation-courses',
  
  // Fallback
  'basic': '/courses',
  'general': '/courses',
  'default': '/courses'
};

/**
 * Maps category to CourseType for backward compatibility
 */
export const CATEGORY_TO_TYPE_MAP: Record<string, CourseType> = {
  'react': 'react',
  'sql': 'sql',
  'python': 'python',
  'ai': 'ai',
  'java': 'java',
  'aws': 'aws',
  'azure': 'azure',
  'gcp': 'gcp',
  'dotnet': 'dotnet',
  'csharp': 'dotnet',
  'data-engineering': 'dataengineering',
  'dataengineering': 'dataengineering',
  'javascript': 'javascript',
  'ml': 'ml',
  'data-science': 'datascience',
  'datascience': 'datascience',
  'powerbi': 'powerbi',
  'tableau': 'tableau',
  'infrastructure': 'infrastructure',
  'frontend-advanced': 'frontend-advanced',
  'testing': 'testing-modern',
  'testing-modern': 'testing-modern',
  'selenium': 'testing-modern',
  'web3-development': 'web3-development',
  'serverless': 'serverless-edge',
  'serverless-edge': 'serverless-edge',
  'rpa-tools': 'rpa-tools',
  'rpa': 'rpa-tools',
  'etl': 'etl',
  'workflow-automation': 'workflow-automation',
  'specialized-ai': 'specialized-ai',
  'database': 'sql',
  // New expanded categories - CRITICAL FIX
  'rust': 'rust',
  'kubernetes-mastery': 'kubernetes-mastery',
  'modern-frontend': 'modern-frontend',
  'cross-platform': 'cross-platform',
  'edge-computing': 'edge-computing',
  'green-tech': 'green-tech',
  'no-code': 'no-code',
  'advanced-api': 'advanced-api',
  'automation': 'workflow-automation'
};

/**
 * Get route prefix for a category - UNIVERSAL FUNCTION
 * @param category - Database category (e.g., 'data-science', 'ai', 'python')
 * @returns Route prefix (e.g., '/datascience-courses', '/ai-courses')
 */
export function getCategoryRoutePrefix(category: string): string {
  const normalizedCategory = category?.toLowerCase().trim();
  return CATEGORY_ROUTE_MAP[normalizedCategory] || CATEGORY_ROUTE_MAP['default'];
}

/**
 * Generate complete module route - UNIVERSAL FUNCTION
 * @param courseId - Course identifier
 * @param moduleId - Module identifier
 * @param category - Database category (optional, will attempt to determine from courseId)
 * @returns Complete module route
 */
export function getModuleRoute(courseId: string, moduleId: string, category?: string): string {
  const prefix = category 
    ? getCategoryRoutePrefix(category)
    : getCategoryRoutePrefix(determineCategoryFromId(courseId));
  
  return `${prefix}/${courseId}/${moduleId}`;
}

/**
 * Generate complete course route - UNIVERSAL FUNCTION
 * @param courseId - Course identifier
 * @param category - Database category (optional)
 * @returns Complete course route
 */
export function getCourseRoute(courseId: string, category?: string): string {
  const prefix = category 
    ? getCategoryRoutePrefix(category)
    : getCategoryRoutePrefix(determineCategoryFromId(courseId));
  
  return `${prefix}/${courseId}`;
}

/**
 * Determine category from course ID patterns (fallback when category not provided)
 * @param courseId - Course identifier
 * @returns Best-guess category
 */
function determineCategoryFromId(courseId: string): string {
  const id = courseId.toLowerCase();
  
  // Check each category pattern
  if (id.includes('sql') || id.startsWith('sql-')) return 'sql';
  if (id.includes('python') || id.startsWith('python-')) return 'python';
  if (id.includes('ai') || id.startsWith('ai-')) return 'ai';
  if (id.includes('java') || id.startsWith('java-')) return 'java';
  if (id.includes('aws') || id.startsWith('aws-')) return 'aws';
  if (id.includes('azure') || id.startsWith('azure-')) return 'azure';
  if (id.includes('gcp') || id.startsWith('gcp-')) return 'gcp';
  if (id.includes('dotnet') || id.includes('csharp')) return 'dotnet';
  if (id.includes('dataengineering') || id.includes('data-engineering')) return 'dataengineering';
  if (id.includes('datascience') || id.includes('data-science')) return 'datascience';
  if (id.includes('javascript') || id.startsWith('js-')) return 'javascript';
  if (id.includes('ml') || id.startsWith('ml-')) return 'ml';
  if (id.includes('powerbi')) return 'powerbi';
  if (id.includes('tableau')) return 'tableau';
  if (id.includes('etl')) return 'etl';
  if (id.includes('workflow') || id.includes('n8n') || id.includes('zapier') || id.includes('make') || id.includes('integromat')) return 'workflow-automation';
  if (id.includes('rpa') || id.includes('uipath') || id.includes('power-automate') || id.includes('automation-anywhere') || id.includes('blue-prism')) return 'rpa-tools';
  if (id.includes('selenium') || id.includes('testing')) return 'testing-modern';
  if (id.includes('react')) return 'react';
  if (id.includes('rust')) return 'rust';
  if (id.includes('kubernetes')) return 'kubernetes-mastery';
  if (id.includes('nvidia') || id.includes('gemini') || id.includes('github-ai')) return 'specialized-ai';
  if (id.includes('flutter') || id.includes('tauri')) return 'cross-platform';
  if (id.includes('svelte') || id.includes('htmx') || id.includes('vue') || id.includes('angular')) return 'modern-frontend';
  
  return 'basic';
}

/**
 * Get CourseType from category for backward compatibility
 * @param category - Database category
 * @returns CourseType enum value
 */
export function getCourseTypeFromCategory(category: string): CourseType {
  const normalizedCategory = category?.toLowerCase().trim();
  return (CATEGORY_TO_TYPE_MAP[normalizedCategory] || 'basic') as CourseType;
}

/**
 * Validate if a category is supported
 * @param category - Category to validate
 * @returns true if category has a mapping
 */
export function isSupportedCategory(category: string): boolean {
  const normalizedCategory = category?.toLowerCase().trim();
  return normalizedCategory in CATEGORY_ROUTE_MAP;
}

/**
 * Get all supported categories
 * @returns Array of all supported category names
 */
export function getAllSupportedCategories(): string[] {
  return Object.keys(CATEGORY_ROUTE_MAP).filter(key => 
    key !== 'default' && key !== 'basic' && key !== 'general'
  );
}
